import type {
  InviteAccountAccess,
  InviteAvailability,
  InviteKind
} from './inviteEntryPolicy'
import {
  createSupabaseRequestHeaders,
  getSupabaseCredentials,
} from './supabaseCredentials'

export interface ResolvedInvite {
  authenticated: boolean
  invite_kind: InviteKind
  invite_status: InviteAvailability
  account_access: InviteAccountAccess
  identity_conflict: boolean
  preview: unknown | null
  error_code: string | null
}

export class InviteResolverError extends Error {
  status: number
  code: string

  constructor(message: string, status: number, code: string) {
    super(message)
    this.name = 'InviteResolverError'
    this.status = status
    this.code = code
  }
}

export async function resolveInvite(token: string): Promise<ResolvedInvite> {
  const { url: supabaseUrl, apiKey } = getSupabaseCredentials()
  if (!supabaseUrl || !apiKey) throw new Error('Missing Supabase credentials for invite resolution')
  const authToken = localStorage.getItem('auth_token')

  const request = (bearer?: string) => fetch(`${supabaseUrl}/functions/v1/resolve-invite`, {
    method: 'POST',
    headers: createSupabaseRequestHeaders(apiKey, bearer, {
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify({ invite_token: token })
  })

  let response = await request(authToken || apiKey)
  // Invite-route публичный. Просроченный JWT не должен превращать использованную ссылку
  // в экран 401: повторяем read-only resolution анонимно, а обычный вход затем сам
  // восстановит или запросит сессию на постоянном адресе приложения.
  if (authToken && response.status === 401) {
    response = await request(apiKey)
  }

  const payload = await response.json().catch(() => ({})) as Partial<ResolvedInvite> & {
    message?: string
  }

  if (!response.ok) {
    throw new InviteResolverError(
      payload.message || 'Не удалось проверить приглашение',
      response.status,
      payload.error_code || 'invite_resolution_failed'
    )
  }

  return payload as ResolvedInvite
}
